/* eslint-disable jsx-a11y/no-autofocus */
import React, { useState } from 'react';
import { InputBaseComponentProps } from '@mui/material/InputBase';
import { utils } from '@rjsf/core';
import { TextField } from '../../stories/TextField';
import { CustomWidgetProps } from '../types';
const { getDisplayLabel } = utils;
const FilledInputWidget = ({
  id,
  placeholder,
  readonly,
  disabled,
  label,
  value,
  onChange,
  onBlur,
  onFocus,
  autofocus,
  options,
  schema,
  uiSchema,
  required,
  formContext: { onHasChanged },
}: CustomWidgetProps): JSX.Element => {
  const [hasChanged, setHasChanged] = useState(false);
  // When the schema accepts null (optional EMD fields), clearing the field sends `null` rather
  // than `''`, so the empty value validates (and reaches the BE as null) instead of tripping the
  // field's own type/pattern/min-max rules. Schemas that don't opt in keep the empty-string default.
  const schemaAllowsNull = Array.isArray(schema.type) && schema.type.includes('null');
  const { emptyValue = required ? undefined : schemaAllowsNull ? null : '' } = options;
  const _onChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    onChange(value === '' ? emptyValue : value);
    setHasChanged(true);
  };
  const _onBlur = ({ target: { value: newVal } }: React.FocusEvent<HTMLInputElement>) => {
    hasChanged && onHasChanged?.({ id, value: newVal });
    onBlur(id, newVal);
    setHasChanged(false);
  };
  const _onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      hasChanged && onHasChanged?.({ id, value });
      setHasChanged(false);
    }
  };
  const _onFocus = ({ target: { value } }: React.FocusEvent<HTMLInputElement>) => onFocus(id, value);
  const displayLabel = getDisplayLabel(schema, uiSchema);
  // `schema.type` may be a union with `null` for optional fields (e.g. ['number', 'null']),
  // so resolve the primary (non-null) type before deciding the input type.
  const primaryType = Array.isArray(schema.type) ? schema.type.find((t) => t !== 'null') : schema.type;
  let inputType = 'text';
  if (primaryType === 'integer' || primaryType === 'number') {
    inputType = 'number';
  }
  const inputProps: InputBaseComponentProps = {};
  if (primaryType === 'number') inputProps.step = 'any';
  return (
    <>
      <TextField
        label={displayLabel ? label || schema.title : ''}
        id={id}
        data-testid="filled-string-input-widget"
        placeholder={placeholder}
        autoFocus={autofocus}
        disabled={disabled}
        type={inputType}
        value={value || value === 0 ? value : ''}
        onChange={_onChange}
        onBlur={_onBlur}
        onFocus={_onFocus}
        inputProps={inputProps}
        onKeyDown={_onKeyDown}
        fullWidth
        slotProps={{
          htmlInput: {
            readOnly: readonly,
            'aria-label': label || schema.title,
          },
        }}
      />
    </>
  );
};
export default FilledInputWidget;
